export type CountryCode = "US" | "UK" | "DE" | "TR" | "Generic";

interface CountrySchedule {
  countryName: string;
  officialSource: string;
  sourceLink: string;
  schedule: Record<number, string[]>;
}

export const VACCINATION_DATA: Record<CountryCode, CountrySchedule> = {
  US: {
    countryName: "United States",
    officialSource: "CDC (Centers for Disease Control and Prevention)",
    sourceLink: "https://www.cdc.gov/vaccines/hcp/imz-schedules/child-adolescent-age.html",
    schedule: {
      0:  ["HepB (Hepatitis B)"],
      8:  ["RV (Rotavirus)", "DTaP (Diphtheria, Tetanus, Pertussis)", "Hib (Haemophilus influenzae type b)", "PCV (Pneumococcal)", "IPV (Polio)"],
      17: ["RV", "DTaP", "Hib", "PCV", "IPV"],
      26: ["DTaP", "PCV", "HepB", "IPV", "Flu (Influenza)"]
    }
  },
  UK: {
    countryName: "United Kingdom",
    officialSource: "NHS (National Health Service)",
    sourceLink: "https://www.nhs.uk/vaccinations/nhs-vaccinations-and-when-to-have-them/",
    schedule: {
      8:  ["6-in-1 (DTaP/IPV/Hib/HepB)", "RV (Rotavirus)", "MenB (Meningococcal B)"],
      12: ["6-in-1", "RV", "PCV (Pneumococcal)"],
      16: ["6-in-1", "MenB"],
      52: ["Hib/MenC", "MMR (Measles, Mumps, Rubella)", "PCV", "MenB"]
    }
  },
  DE: {
    countryName: "Germany",
    officialSource: "RKI / STIKO (Standing Committee on Vaccination)",
    sourceLink: "https://www.rki.de/EN/Topics/Infectious-diseases/Immunisation/STIKO/STIKO-recommendations/STIKO_recommendations_node.html",
    schedule: {
      6:  ["RV (Rotavirus)"],
      8:  ["6-in-1 (DTaP/IPV/Hib/HepB)", "PCV (Pneumococcal)"],
      17: ["6-in-1", "PCV"],
      48: ["6-in-1", "PCV", "MMR (Measles, Mumps, Rubella)", "Varicella (Chickenpox)"]
    }
  },
  TR: {
    countryName: "Turkey",
    officialSource: "Ministry of Health (T.C. Sağlık Bakanlığı)",
    sourceLink: "https://asi.saglik.gov.tr/asi-takvimi",
    schedule: {
      0:  ["HepB (Hepatitis B)"],
      4:  ["HepB"],
      8:  ["BCG (Tuberculosis)", "5-in-1 (DTaP/IPV/Hib)", "PCV (Pneumococcal)"],
      17: ["5-in-1", "PCV"],
      26: ["5-in-1", "PCV", "HepB", "OPA (Oral Polio)"]
    }
  },
  Generic: {
    countryName: "International / Generic",
    officialSource: "WHO (World Health Organization)",
    sourceLink: "https://www.who.int/teams/immunization-vaccines-and-biologicals/policies/who-recommendations-for-routine-immunization---summary-tables",
    schedule: {
      0:  ["BCG (Tuberculosis)", "HepB (Hepatitis B)"],
      6:  ["DTaP", "Hib", "HepB", "IPV", "RV", "PCV"],
      10: ["DTaP", "Hib", "HepB", "IPV", "RV", "PCV"],
      14: ["DTaP", "Hib", "HepB", "IPV", "RV", "PCV"]
    }
  }
};

export function getScheduleWeekForAge(countryCode: string, ageWeeks: number): number | undefined {
  const country = VACCINATION_DATA[countryCode as CountryCode];
  if (!country) return undefined;
  return Object.keys(country.schedule)
    .map(Number)
    .filter((w) => w <= ageWeeks)
    .sort((a, b) => b - a)[0];
}

export function getScheduledVaccines(countryCode: string, ageWeeks: number): string[] {
  const country = VACCINATION_DATA[countryCode as CountryCode];
  if (!country) return [];
  const scheduleWeek = getScheduleWeekForAge(countryCode, ageWeeks);
  if (scheduleWeek === undefined) return [];
  return country.schedule[scheduleWeek] ?? [];
}

export function getAllScheduleWeeks(countryCode: string): number[] {
  const country = VACCINATION_DATA[countryCode as CountryCode];
  if (!country) return [];
  return Object.keys(country.schedule).map(Number).sort((a, b) => a - b);
}
